import { useEffect, useState } from "react";
import { LuCheck } from "react-icons/lu";
import Modal from "../../../common/Modal";
import Button from "../../../common/Buttons";
import TextInput from "../../../common/TextInput";
import PhoneInput from "../../../common/PhoneInput";
import { EMAIL_REGEX } from "../../../utils/Constant";
import { phoneError } from "../../../utils/phone";

const EMPTY_FORM = {
  name: "",
  email: "",
  contactNumber: "",
  designation: "",
  address: "",
  admin: false,
};

export default function UserFormModal({ open, mode, initialValue, onClose, onSubmit, loading }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setForm(mode === "edit" && initialValue ? { ...EMPTY_FORM, ...initialValue } : EMPTY_FORM);
      setTouched({});
    }
  }, [open, mode, initialValue]);

  const markTouched = (field) => () => setTouched((t) => ({ ...t, [field]: true }));
  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nameError = touched.name && !form.name.trim() ? "Name is required." : "";
  const emailError =
    touched.email && !form.email.trim()
      ? "Email is required."
      : touched.email && !EMAIL_REGEX.test(form.email)
      ? "Enter a valid email address."
      : "";

  // Contact number is optional, but must be valid when entered
  const contactError = touched.contactNumber ? phoneError(form.contactNumber) : "";

  const isValid =
    form.name.trim() !== "" &&
    form.email.trim() !== "" &&
    EMAIL_REGEX.test(form.email) &&
    !phoneError(form.contactNumber);

  const handleSubmit = () => {
    setTouched({ name: true, email: true, contactNumber: true });
    if (!isValid) return;
    onSubmit(form);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit User" : "Add User"}
      footer={
        <Button
          variant="solid"
          icon={<LuCheck size={15} />}
          loading={loading}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          {mode === "edit" ? "Save Changes" : "Add User"}
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextInput
            label="Name"
            required
            value={form.name}
            onChange={setField("name")}
            onBlur={markTouched("name")}
            error={nameError}
            placeholder="Full name"
          />
        </div>

        <div className="sm:col-span-2">
          <TextInput
            label="Email"
            required
            type="email"
            value={form.email}
            onChange={setField("email")}
            onBlur={markTouched("email")}
            error={emailError}
            placeholder="name@company.com"
            disabled={mode === "edit"}
          />
        </div>

        <PhoneInput
          label="Contact Number"
          value={form.contactNumber || ""}
          onChange={(value) => setForm((f) => ({ ...f, contactNumber: value }))}
          onBlur={markTouched("contactNumber")}
          error={contactError}
        />
        <TextInput
          label="Designation"
          value={form.designation}
          onChange={setField("designation")}
          placeholder="Job title"
        />

        <div className="sm:col-span-2">
          <TextInput
            label="Address"
            value={form.address}
            onChange={setField("address")}
            placeholder="Address"
          />
        </div>

        <label className="flex items-center gap-2 text-[13px] text-slate-700 sm:col-span-2">
          <input
            type="checkbox"
            checked={form.admin}
            onChange={(e) => setForm((f) => ({ ...f, admin: e.target.checked }))}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
          />
          Grant admin access
        </label>
      </div>
    </Modal>
  );
}